import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '@state';
import {
  BackOfficeApplicationsScheduleFilesToDeletePayload,
  BackOfficeApplicationsRestorePayload,
  BackOfficeApplicationsSoftDeletePayload,
  BackOfficeGenerateApplicationReport,
  BackOfficeGenerateTransactionsReport,
  BackOfficeGetApplicationsPayload,
  BackOfficeGetPartnersPayload,
  BackOfficeGetScheduledFilesDeletionPayload,
  BackOfficeGetTransactionsPayload,
  BackOfficeGetUsersPayload,
  BackOfficeUsersActivateAccountPayload,
  BackOfficeUsersChangePasswordPayload,
  BackOfficeUsersDeactivateAccountPayload,
  BackOfficeUsersPermDeletePayload,
  BackOfficeUsersRestoreAccountPayload,
  BackOfficeUsersSoftDeletePayload,
  User,
  BackOfficeGenerateUsersReport,
} from '@interfaces';
import * as backOfficeActions from './back-office.actions';
import * as backOfficeSelectors from './back-office.selectors';
import { Actions, ofType } from '@ngrx/effects';

@Injectable()
export class BackOfficeFacade {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions = inject(Actions);

  public roles$ = this.store.select(backOfficeSelectors.selectRoles);
  public languages$ = this.store.select(backOfficeSelectors.selectLanguages);
  public companies$ = this.store.select(backOfficeSelectors.selectCompanies);
  public employees$ = this.store.select(backOfficeSelectors.selectEmployees);
  public partners$ = this.store.select(backOfficeSelectors.selectPartners);
  public selectedPartners$ = this.store.select(backOfficeSelectors.selectSelectedPartners);

  public getRolesSuccess$ = this.actions.pipe(ofType(backOfficeActions.getRolesSuccess));
  public getCompaniesSuccess$ = this.actions.pipe(ofType(backOfficeActions.getCompaniesSuccess));
  public getEmployeesSuccess$ = this.actions.pipe(ofType(backOfficeActions.getEmployeesSuccess));
  public getPartnersSuccess$ = this.actions.pipe(ofType(backOfficeActions.getPartnersSuccess));

  // ############
  // USERS
  // ############

  public users$ = this.store.select(backOfficeSelectors.selectUsers);
  public usersLoading$ = this.store.select(backOfficeSelectors.selectUsersLoading);
  public usersErrorMessage$ = this.store.select(backOfficeSelectors.selectUsersErrorMessage);
  public usersPagination$ = this.store.select(backOfficeSelectors.selectUsersLoadedPagination);
  public usersGenerating$ = this.store.select(backOfficeSelectors.selectUsersGenerating);

  public usersActivateAccountSuccess$ = this.actions.pipe(ofType(backOfficeActions.usersActivateAccountSuccess));
  public usersActivateAccountError$ = this.actions.pipe(ofType(backOfficeActions.usersActivateAccountError));
  public usersDeactivateAccountSuccess$ = this.actions.pipe(ofType(backOfficeActions.usersDeactivateAccountSuccess));
  public usersDeactivateAccountError$ = this.actions.pipe(ofType(backOfficeActions.usersDeactivateAccountError));
  public usersSoftDeleteSuccess$ = this.actions.pipe(ofType(backOfficeActions.usersSoftDeleteSuccess));
  public usersSoftDeleteError$ = this.actions.pipe(ofType(backOfficeActions.usersSoftDeleteError));
  public usersRestoreAccountSuccess$ = this.actions.pipe(ofType(backOfficeActions.usersRestoreAccountSuccess));
  public usersRestoreAccountError$ = this.actions.pipe(ofType(backOfficeActions.usersRestoreAccountError));
  public usersChangePasswordSuccess$ = this.actions.pipe(ofType(backOfficeActions.usersChangePasswordSuccess));
  public usersChangePasswordError$ = this.actions.pipe(ofType(backOfficeActions.usersChangePasswordError));

  // ############
  // TRANSACTIONS
  // ############

  public transactions$ = this.store.select(backOfficeSelectors.selectTransactions);
  public transactionsLoading$ = this.store.select(backOfficeSelectors.selectTransactionsLoading);
  public transactionsErrorMessage$ = this.store.select(backOfficeSelectors.selectTransactionsErrorMessage);
  public transactionsPagination$ = this.store.select(backOfficeSelectors.selectTransactionsLoadedPagination);
  public transactionsDownloading$ = this.store.select(backOfficeSelectors.selectTransactionsDownloading);
  public transactionsGenerating$ = this.store.select(backOfficeSelectors.selectTransactionsGenerating);

  // ############
  // APPLICATIONS
  // ############

  public applications$ = this.store.select(backOfficeSelectors.selectApplications);
  public applicationsLoading$ = this.store.select(backOfficeSelectors.selectApplicationsLoading);
  public applicationsErrorMessage$ = this.store.select(backOfficeSelectors.selectApplicationsErrorMessage);
  public applicationsPagination$ = this.store.select(backOfficeSelectors.selectApplicationsLoadedPagination);
  public applicationsGenerating$ = this.store.select(backOfficeSelectors.selectApplicationsGenerating);
  public applicationsSoftDeleteSuccess$ = this.actions.pipe(ofType(backOfficeActions.applicationsSoftDeleteSuccess));
  public applicationsSoftDeleteError$ = this.actions.pipe(ofType(backOfficeActions.applicationsSoftDeleteError));
  public scheduleUserProcessesFilesToDeleteSuccess$ = this.actions.pipe(
    ofType(backOfficeActions.scheduleUserProcessesFilesToDeleteSuccess)
  );
  public scheduleUserProcessesFilesToDeleteError$ = this.actions.pipe(
    ofType(backOfficeActions.scheduleUserProcessesFilesToDeleteError)
  );
  public applicationsRestoreSuccess$ = this.actions.pipe(ofType(backOfficeActions.applicationsRestoreSuccess));
  public applicationsRestoreError$ = this.actions.pipe(ofType(backOfficeActions.applicationsRestoreeError));

  // ############
  // SCHEDULED FILES DELETION
  // ############

  public scheduledFilesDeletion$ = this.store.select(backOfficeSelectors.selectScheduledFilesDeletion);
  public scheduledFilesDeletionLoading$ = this.store.select(backOfficeSelectors.selectScheduledFilesDeletionLoading);
  public scheduledFilesDeletionErrorMessage$ = this.store.select(
    backOfficeSelectors.selectScheduledFilesDeletionErrorMessage
  );
  public scheduledFilesDeletionPagination$ = this.store.select(
    backOfficeSelectors.selectScheduledFilesDeletionLoadedPagination
  );
  public deleteScheduledFilesSuccess$ = this.actions.pipe(ofType(backOfficeActions.deleteScheduledFilesSuccess));
  public deleteScheduledFilesError$ = this.actions.pipe(ofType(backOfficeActions.deleteScheduledFilesError));
  public restoreScheduledFilesSuccess$ = this.actions.pipe(ofType(backOfficeActions.restoreScheduledFilesSuccess));
  public restoreScheduledFilesError$ = this.actions.pipe(ofType(backOfficeActions.restoreScheduledFilesError));

  // ############
  // MANAGE ASSETS
  // ############

  public userProcessManageFiles$ = this.store.select(backOfficeSelectors.selectUserProcessManageFiles);
  public getUserProcessBasicInformationSuccess$ = this.actions.pipe(
    ofType(backOfficeActions.getUserProcessBasicInformationSuccess)
  );
  public getUserProcessBasicInformationError$ = this.actions.pipe(
    ofType(backOfficeActions.getUserProcessBasicInformationError)
  );
  public getUserProcessManageFilesSuccess$ = this.actions.pipe(
    ofType(backOfficeActions.getUserProcessManageFilesSuccess)
  );
  public getUserProcessManageFilesError$ = this.actions.pipe(ofType(backOfficeActions.getUserProcessManageFilesError));
  public manageFilesScheduleToDeleteSuccess$ = this.actions.pipe(
    ofType(backOfficeActions.manageFilesScheduleToDeleteSuccess)
  );
  public manageFilesScheduleToDeleteError$ = this.actions.pipe(
    ofType(backOfficeActions.manageFilesScheduleToDeleteError)
  );
  public manageFilesScheduleToDeleteAllSuccess$ = this.actions.pipe(
    ofType(backOfficeActions.manageFilesScheduleToDeleteAllSuccess)
  );
  public manageFilesScheduleToDeleteAllError$ = this.actions.pipe(
    ofType(backOfficeActions.manageFilesScheduleToDeleteAllError)
  );
  public manageFilesRestoreSuccess$ = this.actions.pipe(ofType(backOfficeActions.manageFilesRestoreSuccess));
  public manageFilesRestoreError$ = this.actions.pipe(ofType(backOfficeActions.manageFilesRestoreError));
  public manageFilesRestoreAllSuccess$ = this.actions.pipe(ofType(backOfficeActions.manageFilesRestoreAllSuccess));
  public manageFilesRestoreAllError$ = this.actions.pipe(ofType(backOfficeActions.manageFilesRestoreAllError));

  public partnersLoading$ = this.store.select(backOfficeSelectors.selectPartnersLoading);

  public getCompanies(): void {
    this.store.dispatch(backOfficeActions.getCompanies());
  }

  public getRoles(): void {
    this.store.dispatch(backOfficeActions.getRoles());
  }

  public getLanguages(): void {
    this.store.dispatch(backOfficeActions.getLanguages());
  }

  public getEmployees(): void {
    this.store.dispatch(backOfficeActions.getEmployees());
  }

  public getPartners(payload: BackOfficeGetPartnersPayload): void {
    this.store.dispatch(backOfficeActions.getPartners({ payload }));
  }

  public setSelectedPartners(payload: { selectedPartners: Partial<User>[] }): void {
    this.store.dispatch(backOfficeActions.setSelectedPartners({ payload }));
  }

  // ############
  // Users
  // ############

  public getUsers(payload: BackOfficeGetUsersPayload): void {
    this.store.dispatch(backOfficeActions.getUsers({ payload }));
  }

  public usersActivateAccount(payload: BackOfficeUsersActivateAccountPayload): void {
    this.store.dispatch(backOfficeActions.usersActivateAccount({ payload }));
  }

  public usersDeactivateAccount(payload: BackOfficeUsersDeactivateAccountPayload): void {
    this.store.dispatch(backOfficeActions.usersDeactivateAccount({ payload }));
  }

  public usersRestoreAccount(payload: BackOfficeUsersRestoreAccountPayload): void {
    this.store.dispatch(backOfficeActions.usersRestoreAccount({ payload }));
  }

  public usersSoftDelete(payload: BackOfficeUsersSoftDeletePayload): void {
    this.store.dispatch(backOfficeActions.usersSoftDelete({ payload }));
  }

  public usersPermDelete(payload: BackOfficeUsersPermDeletePayload): void {
    this.store.dispatch(backOfficeActions.usersPermDelete({ payload }));
  }

  public usersChangePassword(payload: BackOfficeUsersChangePasswordPayload): void {
    this.store.dispatch(backOfficeActions.usersChangePassword({ payload }));
  }

  public usersGenerateReport(payload: BackOfficeGenerateUsersReport): void {
    this.store.dispatch(backOfficeActions.usersGenerateReport({ payload }));
  }

  // ############
  // Transactions
  // ############

  public getTransactions(payload: BackOfficeGetTransactionsPayload): void {
    this.store.dispatch(backOfficeActions.getTransactions({ payload }));
  }

  public transactionsDownloadPurchaseDocument(transactionID: string, previewTargetKey?: string): void {
    this.store.dispatch(backOfficeActions.transactionsDownloadPurchaseDocument({ transactionID, previewTargetKey }));
  }

  public transactionsGenerateReport(payload: BackOfficeGenerateTransactionsReport): void {
    this.store.dispatch(backOfficeActions.transactionsGenerateReport({ payload }));
  }

  // ############
  // Applications
  // ############

  public getApplications(payload: BackOfficeGetApplicationsPayload): void {
    this.store.dispatch(backOfficeActions.getApplications({ payload }));
  }

  public applicationsGenerateReport(payload: BackOfficeGenerateApplicationReport): void {
    this.store.dispatch(backOfficeActions.applicationsGenerateReport({ payload }));
  }

  public applicationsSoftDelete(payload: BackOfficeApplicationsSoftDeletePayload): void {
    this.store.dispatch(backOfficeActions.applicationsSoftDelete({ payload }));
  }

  public applicationsRestore(payload: BackOfficeApplicationsRestorePayload): void {
    this.store.dispatch(backOfficeActions.applicationsRestore({ payload }));
  }

  public scheduleUserProcessesFilesToDelete(payload: BackOfficeApplicationsScheduleFilesToDeletePayload): void {
    this.store.dispatch(backOfficeActions.scheduleUserProcessesFilesToDelete({ payload }));
  }

  // ############
  // SCHEDULED FILES DELETION
  // ############

  public getScheduledFilesDeletion(payload: BackOfficeGetScheduledFilesDeletionPayload): void {
    this.store.dispatch(backOfficeActions.getScheduledFilesDeletion({ payload }));
  }

  public deleteScheduledFiles(payload: { filesIdsToDelete: string[] }): void {
    this.store.dispatch(backOfficeActions.deleteScheduledFiles({ payload }));
  }

  public restoreScheduledFiles(payload: { filesIdsToRestore: string[] }): void {
    this.store.dispatch(backOfficeActions.restoreScheduledFiles({ payload }));
  }

  // ############
  // MANAGE FILES
  // ############

  public getUserProcessBasicInformation(userProcessId: string): void {
    this.store.dispatch(backOfficeActions.getUserProcessBasicInformation({ userProcessId }));
  }

  public getUserProcessManageFiles(userProcessId: string): void {
    this.store.dispatch(backOfficeActions.getUserProcessManageFiles({ userProcessId }));
  }

  public manageFilesDownload(payload: {
    userProcessId: string;
    fileId: string;
    fileName: string;
    fileType: 'userDocuments' | 'userAssets';
  }): void {
    this.store.dispatch(backOfficeActions.manageFilesDownload({ payload }));
  }

  public manageFilesScheduleToDelete(payload: {
    userProcessId: string;
    fileId: string;
    fileName: string;
    fileType: 'userDocuments' | 'userAssets';
  }): void {
    this.store.dispatch(backOfficeActions.manageFilesScheduleToDelete({ payload }));
  }

  public manageFilesScheduleToDeleteAll(payload: { userProcessId: string }): void {
    this.store.dispatch(backOfficeActions.manageFilesScheduleToDeleteAll({ payload }));
  }

  public manageFilesRestore(payload: {
    userProcessId: string;
    fileId: string;
    fileName: string;
    fileType: 'userDocuments' | 'userAssets';
  }): void {
    this.store.dispatch(backOfficeActions.manageFilesRestore({ payload }));
  }

  public manageFilesRestoreAll(payload: { userProcessId: string }): void {
    this.store.dispatch(backOfficeActions.manageFilesRestoreAll({ payload }));
  }
}
